<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useQaRequest } from 'dashboard/composables/useQaRequest';
import QaDashboardApi from 'dashboard/api/qaDashboard';
import { parseSummaryPayload } from 'dashboard/helper/qaApiSchema';
import '../styles/qaShell.css';

// Stable compile marker for release verification (must appear in dashboard bundle).
const QA_DAILY_AUDIT_SUMMARY_CARD_V1 = 'QA_DAILY_AUDIT_SUMMARY_CARD_V1';
// QA_FE_FOUNDATION_V1 + QA_SUMMARY_ARIA_BUSY_V1 + QA_FE_OBS_I18N_V1 + QA_FE_A11Y_SUMMARY_V1
const { t } = useI18n();
const showAlert = useAlert;
const { accountScopedRoute } = useAccount();
const { isLoading, run: runRequest } = useQaRequest();

const days = ref(14);
const summary = reactive({
  open: {},
  template: {},
  safety: {},
  review_sla: {},
  trends: { by_language: [], by_inbox: [], by_team: [], by_agent: [] },
  delivery: {},
  observability: {
    false_positive: {},
    precision: {},
    alert_latency: {},
    message_processing: {},
    ai_cost: {},
    daily_audit_ops: {},
    fact_watermark: {},
    kpi_data_quality: {},
    topic_cluster: {},
    daily_report_delivery: {},
    live_counters: {},
    job_heartbeats: {},
  },
  rollout: {},
  daily_audit: {},
});

const emptySummary = () => ({
  open: {},
  template: {},
  safety: {},
  review_sla: {},
  trends: { by_language: [], by_inbox: [], by_team: [], by_agent: [] },
  delivery: {},
  observability: {
    false_positive: {},
    precision: {},
    alert_latency: {},
    message_processing: {},
    ai_cost: {},
    daily_audit_ops: {},
    fact_watermark: {},
    kpi_data_quality: {},
    topic_cluster: {},
    daily_report_delivery: {},
    live_counters: {},
    job_heartbeats: {},
  },
  rollout: {},
  daily_audit: {},
});

const pct = value => `${((Number(value) || 0) * 100).toFixed(1)}%`;
const num = value => Number(value || 0);

const assignSummary = item => {
  Object.assign(summary.open, item.open || {});
  Object.assign(summary.template, item.template || {});
  Object.assign(summary.safety, item.safety || {});
  Object.assign(summary.review_sla, item.review_sla || {});
  summary.trends = item.trends || {
    by_language: [],
    by_inbox: [],
    by_team: [],
    by_agent: [],
  };
  Object.assign(summary.delivery, item.delivery || {});
  summary.observability = item.observability || {
    false_positive: {},
    precision: {},
    alert_latency: {},
    message_processing: {},
    ai_cost: {},
    daily_audit_ops: {},
    fact_watermark: {},
    kpi_data_quality: {},
    topic_cluster: {},
    daily_report_delivery: {},
    live_counters: {},
    job_heartbeats: {},
  };
  summary.rollout = item.rollout || {};
  summary.daily_audit = item.daily_audit || {};
};

const loadSummary = async () => {
  // P2-FE-007: clear stale window values before refresh completes.
  Object.assign(summary, emptySummary());
  try {
    await runRequest(async () => {
      const response = await QaDashboardApi.getSummary({ days: days.value });
      const parsed = parseSummaryPayload(response?.data || {});
      if (!parsed.ok)
        throw new Error(parsed.error || 'Invalid summary payload');
      assignSummary(parsed.item);
      return parsed.item;
    });
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        error.message ||
        t('QA_DASHBOARD.SUMMARY.ERROR_LOAD')
    );
  }
};

const drillTo = (name, query = {}) => accountScopedRoute(name, {}, query);

const trendRows = computed(() => [
  {
    title: t('QA_DASHBOARD.SUMMARY.TREND_LANGUAGE'),
    rows: summary.trends.by_language || [],
    param: 'language',
  },
  {
    title: t('QA_DASHBOARD.SUMMARY.TREND_INBOX'),
    rows: summary.trends.by_inbox || [],
    param: 'inbox_id',
  },
  {
    title: t('QA_DASHBOARD.SUMMARY.TREND_TEAM'),
    rows: summary.trends.by_team || [],
    param: 'team_id',
  },
  {
    title: t('QA_DASHBOARD.SUMMARY.TREND_AGENT'),
    rows: summary.trends.by_agent || [],
    param: 'agent_user_id',
  },
]);

const rolloutLabel = mode => {
  const key = String(mode || 'unset');
  return t(`QA_DASHBOARD.SUMMARY.ROLLOUT_${key.toUpperCase()}`, key);
};

const dailyAudit = computed(() => summary.daily_audit || {});
const dailyToday = computed(() => dailyAudit.value.today || null);
const dailyIntegrity = computed(() => dailyAudit.value.integrity || {});
const dailySchedule = computed(() => dailyAudit.value.schedule_preview || {});
const dailyAuditMarker = computed(() => QA_DAILY_AUDIT_SUMMARY_CARD_V1);

// Daily report schedule (admin-editable). PATCHes qa_dashboard/daily_audit_settings.
const dailyAuditSettings = reactive({
  localRunTime: '22:00',
  graceMinutes: 60,
  enabled: false,
  overviewChannel: 'in_app',
  overviewRef: '',
  deepChannel: 'in_app',
  deepRef: '',
});
const dailyAuditMeta = reactive({
  schedulerInstalled: false,
  effectiveTimezone: '',
  settingsVersion: 0,
});
const isDailyAuditSaving = ref(false);

const applyDailyAuditItem = item => {
  const destinations = item.destinations || {};
  dailyAuditSettings.localRunTime = item.local_run_time || '22:00';
  dailyAuditSettings.graceMinutes = Number(item.grace_minutes ?? 60);
  dailyAuditSettings.enabled = item.enabled === true;
  dailyAuditSettings.overviewChannel =
    destinations.overview?.channel || 'in_app';
  dailyAuditSettings.overviewRef = destinations.overview?.ref || '';
  dailyAuditSettings.deepChannel = destinations.deep?.channel || 'in_app';
  dailyAuditSettings.deepRef = destinations.deep?.ref || '';
  dailyAuditMeta.schedulerInstalled = item.scheduler_installed === true;
  dailyAuditMeta.effectiveTimezone = item.effective_timezone || '';
  dailyAuditMeta.settingsVersion = Number(item.settings_version || 0);
};

const loadDailyAuditSettings = async () => {
  try {
    const response = await QaDashboardApi.getDailyAuditSettings();
    applyDailyAuditItem(response?.data?.item || {});
  } catch (error) {
    // non-fatal; card falls back to defaults
  }
};

const saveDailyAuditSettings = async () => {
  isDailyAuditSaving.value = true;
  try {
    const response = await QaDashboardApi.updateDailyAuditSettings({
      local_run_time: dailyAuditSettings.localRunTime,
      grace_minutes: dailyAuditSettings.graceMinutes,
      enabled: dailyAuditSettings.enabled,
      settings_version: dailyAuditMeta.settingsVersion,
      destinations: {
        overview: {
          channel: dailyAuditSettings.overviewChannel,
          ref: dailyAuditSettings.overviewRef,
        },
        deep: {
          channel: dailyAuditSettings.deepChannel,
          ref: dailyAuditSettings.deepRef,
        },
      },
    });
    applyDailyAuditItem(response?.data?.item || {});
    showAlert('Daily report schedule saved.');
  } catch (error) {
    showAlert(error.response?.data?.error || 'Could not save the schedule.');
  } finally {
    isDailyAuditSaving.value = false;
  }
};

onMounted(() => {
  loadSummary();
  loadDailyAuditSettings();
});
</script>

<template>
  <div class="qa-page" :aria-busy="isLoading ? 'true' : 'false'">
    <div
      class="qa-shell"
      role="main"
      :aria-label="t('QA_DASHBOARD.SUMMARY.OBS_A11Y_MAIN')"
    >
      <div class="qa-header">
        <div class="qa-header-left">
          <h1 class="qa-title">{{ t('QA_DASHBOARD.SUMMARY.TITLE') }}</h1>
        </div>
        <div class="qa-header-actions">
          <label class="qa-field" style="min-width: 120px; margin: 0">
            <span class="qa-label">{{
              t('QA_DASHBOARD.SUMMARY.WINDOW_DAYS')
            }}</span>
            <select
              v-model.number="days"
              class="qa-select"
              @change="loadSummary"
            >
              <option :value="7">7</option>
              <option :value="14">14</option>
              <option :value="30">30</option>
            </select>
          </label>
          <button
            type="button"
            class="qa-btn"
            :disabled="isLoading"
            @click="loadSummary"
          >
            {{
              isLoading
                ? t('QA_DASHBOARD.SUMMARY.LOADING')
                : t('QA_DASHBOARD.SUMMARY.REFRESH')
            }}
          </button>
        </div>
      </div>

      <div class="qa-subheader">
        <p class="qa-desc">{{ t('QA_DASHBOARD.SUMMARY.DESCRIPTION') }}</p>
      </div>

      <div class="qa-content">
        <div class="qa-kpis">
          <router-link
            class="qa-kpi qa-kpi--total"
            :to="drillTo('qa_monitor_incidents', { tab: 'open' })"
          >
            <div class="qa-kpi-label">{{ t('QA_DASHBOARD.SUMMARY.OPEN') }}</div>
            <div class="qa-kpi-value">{{ num(summary.open.total) }}</div>
          </router-link>
          <router-link
            class="qa-kpi"
            :to="drillTo('qa_monitor_incidents', { severity: 'high' })"
          >
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.SUMMARY.HIGH_SEVERITY') }}
            </div>
            <div class="qa-kpi-value qa-kpi-value--bad">
              {{ num(summary.open.high_severity) }}
            </div>
          </router-link>
          <router-link
            class="qa-kpi"
            :to="drillTo('qa_monitor_incidents', { tab: 'overdue' })"
          >
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.SUMMARY.OVERDUE') }}
            </div>
            <div class="qa-kpi-value qa-kpi-value--warn">
              {{ num(summary.open.overdue) }}
            </div>
          </router-link>
          <div class="qa-kpi">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.SUMMARY.DELIVERY_SUCCESS') }}
            </div>
            <div class="qa-kpi-value qa-kpi-value--good">
              {{ pct(summary.delivery.success_rate) }}
            </div>
          </div>
        </div>

        <section
          class="qa-card"
          data-qa-marker="daily-audit-summary-card-20260731.14"
          :data-qa-compile-marker="dailyAuditMarker"
        >
          <div class="qa-card-header">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_CARD') }}
            </h2>
          </div>
          <div class="qa-card-body">
            <div
              v-if="dailyAudit.calibration_only !== false"
              class="qa-banner qa-banner--warn"
              role="status"
            >
              {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_CALIBRATION_BANNER') }}
            </div>

            <p v-if="dailyAudit.error" class="qa-hint" style="margin: 8px 0 0">
              {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_UNAVAILABLE') }}
            </p>

            <div
              v-else
              class="qa-form-grid qa-form-grid--3"
              style="margin-top: 12px"
            >
              <div class="qa-hint-block">
                <div class="qa-label">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_BUSINESS_DATE') }}
                </div>
                <p class="qa-hint">
                  {{
                    dailyAudit.business_date ||
                    t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_NONE')
                  }}
                </p>
              </div>
              <div class="qa-hint-block">
                <div class="qa-label">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_TIMEZONE') }}
                </div>
                <p class="qa-hint">
                  {{
                    dailyAudit.timezone ||
                    t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_NONE')
                  }}
                </p>
              </div>
              <div class="qa-hint-block">
                <div class="qa-label">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_AVAILABLE') }}
                </div>
                <p class="qa-hint">
                  {{
                    dailyAudit.available
                      ? t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_AVAILABLE_YES')
                      : t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_AVAILABLE_NO')
                  }}
                </p>
              </div>
              <div class="qa-hint-block">
                <div class="qa-label">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_STATUS') }}
                </div>
                <p class="qa-hint">
                  {{
                    (dailyToday && dailyToday.status) ||
                    t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_NONE')
                  }}
                </p>
              </div>
              <div class="qa-hint-block">
                <div class="qa-label">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_REVISION') }}
                </div>
                <p class="qa-hint">
                  {{
                    dailyToday && dailyToday.revision != null
                      ? dailyToday.revision
                      : t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_NONE')
                  }}
                </p>
              </div>
              <div class="qa-hint-block">
                <div class="qa-label">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_WATERMARK') }}
                </div>
                <p class="qa-hint">
                  {{
                    dailyIntegrity.watermarked
                      ? t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_WATERMARK_YES')
                      : t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_WATERMARK_NO')
                  }}
                </p>
              </div>
              <div class="qa-hint-block">
                <div class="qa-label">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_COMPLETENESS') }}
                </div>
                <p class="qa-hint">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_COMPLETENESS_NOTE') }}
                </p>
              </div>
              <div class="qa-hint-block">
                <div class="qa-label">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_SCHEDULER') }}
                </div>
                <p class="qa-hint">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_SCHEDULER_OFF') }}
                </p>
              </div>
              <div class="qa-hint-block">
                <div class="qa-label">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_DELIVERY') }}
                </div>
                <p class="qa-hint">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_DELIVERY_OFF') }}
                </p>
              </div>
              <div class="qa-hint-block" style="grid-column: 1 / -1">
                <div class="qa-label">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_NEXT_RUN') }}
                </div>
                <p class="qa-hint">
                  {{
                    dailySchedule.next_run_local ||
                    t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_NONE')
                  }}
                </p>
                <p class="qa-hint" style="margin-top: 4px">
                  {{ t('QA_DASHBOARD.SUMMARY.DAILY_AUDIT_PREVIEW_ONLY') }}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section class="qa-card" data-qa-marker="daily-report-schedule-card">
          <div class="qa-card-header">
            <h2 class="qa-card-title">Daily Report Schedule</h2>
          </div>
          <div class="qa-card-body">
            <p class="qa-hint" style="margin: 0 0 12px">
              The daily agent builds an overview + deep report at the local run
              time and delivers them to Telegram.
            </p>
            <div class="qa-form-grid">
              <label class="qa-field">
                <span class="qa-label">Run time (local)</span>
                <input
                  v-model="dailyAuditSettings.localRunTime"
                  type="time"
                  class="qa-input"
                />
              </label>
              <label class="qa-field">
                <span class="qa-label">Grace minutes</span>
                <input
                  v-model.number="dailyAuditSettings.graceMinutes"
                  type="number"
                  min="0"
                  max="1440"
                  class="qa-input"
                />
              </label>
              <label class="qa-field">
                <span class="qa-label">Timezone</span>
                <input
                  :value="dailyAuditMeta.effectiveTimezone"
                  class="qa-input"
                  disabled
                />
              </label>
            </div>
            <div class="qa-form-grid">
              <label class="qa-field">
                <span class="qa-label">Overview → channel</span>
                <select
                  v-model="dailyAuditSettings.overviewChannel"
                  class="qa-select"
                >
                  <option value="in_app">In-app</option>
                  <option value="telegram">Telegram</option>
                </select>
              </label>
              <label class="qa-field">
                <span class="qa-label">Overview → destination id</span>
                <input
                  v-model="dailyAuditSettings.overviewRef"
                  class="qa-input"
                  placeholder="Alert Hub destination id"
                />
              </label>
              <label class="qa-field">
                <span class="qa-label">Deep → channel</span>
                <select
                  v-model="dailyAuditSettings.deepChannel"
                  class="qa-select"
                >
                  <option value="in_app">In-app</option>
                  <option value="telegram">Telegram</option>
                </select>
              </label>
              <label class="qa-field">
                <span class="qa-label">Deep → destination id</span>
                <input
                  v-model="dailyAuditSettings.deepRef"
                  class="qa-input"
                  placeholder="Alert Hub destination id"
                />
              </label>
            </div>
            <label
              class="qa-checkline"
              style="
                display: flex;
                gap: 8px;
                align-items: center;
                margin: 10px 0;
              "
            >
              <input
                v-model="dailyAuditSettings.enabled"
                type="checkbox"
                :disabled="!dailyAuditMeta.schedulerInstalled"
              />
              <span>Enable daily report auto-send</span>
            </label>
            <p
              v-if="!dailyAuditMeta.schedulerInstalled"
              class="qa-hint"
              style="margin: 0 0 10px"
            >
              Scheduler is not armed in this environment yet — you can set the
              time now; the enable switch activates once an administrator arms
              it.
            </p>
            <button
              type="button"
              class="qa-btn"
              :disabled="isDailyAuditSaving"
              @click="saveDailyAuditSettings"
            >
              {{ isDailyAuditSaving ? 'Saving…' : 'Save schedule' }}
            </button>
          </div>
        </section>

        <section class="qa-card">
          <div class="qa-card-header">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.SUMMARY.TEMPLATE_CARD') }}
            </h2>
            <router-link
              class="qa-btn qa-btn--sm"
              :to="drillTo('qa_dashboard_template_audit')"
            >
              {{ t('QA_DASHBOARD.SUMMARY.OPEN_PAGE') }}
            </router-link>
          </div>
          <div class="qa-card-body">
            <div class="qa-kpis">
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.COMPLIANCE') }}
                </div>
                <div class="qa-kpi-value">
                  {{ pct(summary.template.compliance_rate) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.STATS.CORRECT') }} /
                  {{ t('QA_DASHBOARD.STATS.TOTAL') }}
                </div>
                <div class="qa-kpi-value qa-kpi-value--good">
                  {{ num(summary.template.correct) }} /
                  {{ num(summary.template.total) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.CONFIRMED_RATE') }}
                </div>
                <div class="qa-kpi-value">
                  {{ pct(summary.template.confirmed_rate) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.FALSE_POSITIVE_RATE') }}
                </div>
                <div class="qa-kpi-value qa-kpi-value--bad">
                  {{ pct(summary.template.false_positive_rate) }}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="qa-card">
          <div class="qa-card-header">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.SUMMARY.SAFETY_CARD') }}
            </h2>
            <router-link
              class="qa-btn qa-btn--sm"
              :to="drillTo('qa_dashboard_safety_audit')"
            >
              {{ t('QA_DASHBOARD.SUMMARY.OPEN_PAGE') }}
            </router-link>
          </div>
          <div class="qa-card-body">
            <div class="qa-kpis">
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.PENDING') }}
                </div>
                <div class="qa-kpi-value qa-kpi-value--warn">
                  {{ num(summary.safety.pending_review) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.CONFIRMED_RATE') }}
                </div>
                <div class="qa-kpi-value">
                  {{ pct(summary.safety.confirmed_rate) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.FALSE_POSITIVE_RATE') }}
                </div>
                <div class="qa-kpi-value qa-kpi-value--bad">
                  {{ pct(summary.safety.false_positive_rate) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.REVIEW_SLA_ACK') }}
                </div>
                <div class="qa-kpi-value">
                  {{
                    summary.review_sla.median_ack_hours == null
                      ? '-'
                      : `${summary.review_sla.median_ack_hours}h`
                  }}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="qa-card">
          <div class="qa-card-header">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.SUMMARY.DELIVERY_CARD') }}
            </h2>
            <router-link
              class="qa-btn qa-btn--sm"
              :to="drillTo('qa_dashboard_alert_hub')"
            >
              {{ t('QA_DASHBOARD.SUMMARY.OPEN_PAGE') }}
            </router-link>
          </div>
          <div class="qa-card-body">
            <div class="qa-kpis">
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.DELIVERED_24H') }}
                </div>
                <div class="qa-kpi-value qa-kpi-value--good">
                  {{ num(summary.delivery.delivered_24h) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.FAILED_24H') }}
                </div>
                <div class="qa-kpi-value qa-kpi-value--bad">
                  {{ num(summary.delivery.failed_24h) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">Retry pending</div>
                <div class="qa-kpi-value qa-kpi-value--warn">
                  {{ num(summary.delivery.retry_pending_24h) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">Dead set</div>
                <div class="qa-kpi-value qa-kpi-value--bad">
                  {{ num(summary.delivery.dead_set_24h) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.SKIPPED_24H') }}
                </div>
                <div class="qa-kpi-value qa-kpi-value--warn">
                  {{ num(summary.delivery.skipped_24h) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.ACTIVE_WATCHES') }}
                </div>
                <div class="qa-kpi-value">
                  {{ num(summary.delivery.active_watches) }}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="qa-card" aria-labelledby="qa-obs-heading">
          <div class="qa-card-header">
            <h2 id="qa-obs-heading" class="qa-card-title">
              {{ t('QA_DASHBOARD.SUMMARY.OBS_CARD') }}
            </h2>
          </div>
          <div class="qa-card-body">
            <div class="qa-kpis">
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_COMBINED_PRECISION') }}
                </div>
                <div class="qa-kpi-value">
                  {{
                    pct(summary.observability.precision?.combined?.precision)
                  }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_ALERT_LATENCY') }}
                </div>
                <div class="qa-kpi-value">
                  {{
                    summary.observability.alert_latency?.p95_seconds == null
                      ? '-'
                      : `${summary.observability.alert_latency.p50_seconds ?? '-'} / ${summary.observability.alert_latency.p95_seconds} / ${summary.observability.alert_latency.p99_seconds ?? '-'}s`
                  }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_TG_RETRY_DEAD') }}
                </div>
                <div class="qa-kpi-value">
                  {{ num(summary.observability.alert_latency?.retry_pending) }}
                  /
                  {{ num(summary.observability.alert_latency?.dead_set) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_INCIDENTS_WINDOW') }}
                </div>
                <div class="qa-kpi-value">
                  {{
                    num(
                      summary.observability.message_processing
                        ?.incidents_created
                    )
                  }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_DETECTOR_ERRORS') }}
                </div>
                <div class="qa-kpi-value qa-kpi-value--bad">
                  {{
                    num(
                      summary.observability.message_processing?.detector_errors
                    )
                  }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_AI_FLAGGED') }}
                </div>
                <div class="qa-kpi-value">
                  {{ num(summary.observability.ai_cost?.ai_flagged_incidents) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_AI_CANDIDATE_RATIO') }}
                </div>
                <div class="qa-kpi-value">
                  {{
                    pct(
                      summary.observability.ai_cost?.contextual_candidate_ratio
                    )
                  }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_AI_CALLS_TODAY') }}
                </div>
                <div class="qa-kpi-value">
                  {{
                    num(summary.observability.ai_cost?.moderation_calls_today)
                  }}
                  /
                  {{
                    num(summary.observability.ai_cost?.contextual_calls_today)
                  }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_DAILY_AUDIT_QRCF') }}
                </div>
                <div class="qa-kpi-value">
                  {{ num(summary.observability.daily_audit_ops?.queued) }}
                  /
                  {{ num(summary.observability.daily_audit_ops?.running) }}
                  /
                  {{ num(summary.observability.daily_audit_ops?.completed) }}
                  /
                  {{ num(summary.observability.daily_audit_ops?.failed) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_LATE_EVENTS') }}
                </div>
                <div class="qa-kpi-value qa-kpi-value--warn">
                  {{
                    num(summary.observability.fact_watermark?.late_event_count)
                  }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_DIRTY_DAYS') }}
                </div>
                <div class="qa-kpi-value">
                  {{
                    num(
                      summary.observability.fact_watermark?.dirty_business_days
                    )
                  }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_MISSING_AGENT') }}
                </div>
                <div class="qa-kpi-value">
                  {{
                    pct(
                      summary.observability.kpi_data_quality?.missing_agent_rate
                    )
                  }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_CLUSTER_COVERAGE') }}
                </div>
                <div class="qa-kpi-value">
                  {{ pct(summary.observability.topic_cluster?.coverage) }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_EMBED_PENDING_READY') }}
                </div>
                <div class="qa-kpi-value">
                  {{
                    num(
                      summary.observability.topic_cluster
                        ?.embedding_cache_pending
                    )
                  }}
                  /
                  {{
                    num(
                      summary.observability.topic_cluster?.embedding_cache_ready
                    )
                  }}
                </div>
              </div>
              <div class="qa-kpi">
                <div class="qa-kpi-label">
                  {{ t('QA_DASHBOARD.SUMMARY.OBS_REPORT_DELIVERED_DEAD') }}
                </div>
                <div class="qa-kpi-value">
                  {{
                    num(summary.observability.daily_report_delivery?.delivered)
                  }}
                  /
                  {{ num(summary.observability.daily_report_delivery?.dead) }}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="qa-card">
          <div class="qa-card-header">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.SUMMARY.TRENDS') }}
            </h2>
          </div>
          <div class="qa-card-body">
            <div class="qa-form-grid qa-form-grid--2">
              <div
                v-for="block in trendRows"
                :key="block.title"
                class="qa-hint-block"
              >
                <div class="qa-label">{{ block.title }}</div>
                <ul class="qa-hint" style="margin: 8px 0 0; padding-left: 18px">
                  <li
                    v-for="row in block.rows"
                    :key="`${block.param}-${row.key}`"
                  >
                    <router-link
                      :to="
                        drillTo('qa_monitor_incidents', {
                          [block.param]: row.key,
                        })
                      "
                    >
                      {{ row.label }} ({{ row.count }})
                    </router-link>
                  </li>
                  <li v-if="!block.rows.length">
                    {{ t('QA_DASHBOARD.SUMMARY.NO_TRENDS') }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section class="qa-card">
          <div class="qa-card-header">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.SUMMARY.ROLLOUT') }}
            </h2>
          </div>
          <div class="qa-card-body">
            <div class="qa-form-grid qa-form-grid--3">
              <div class="qa-hint-block">
                <div class="qa-label">Template Audit</div>
                <p class="qa-hint">
                  {{ rolloutLabel(summary.rollout.template_audit?.mode) }}
                </p>
              </div>
              <div class="qa-hint-block">
                <div class="qa-label">Safety</div>
                <p class="qa-hint">
                  {{ rolloutLabel(summary.rollout.safety?.mode) }}
                </p>
              </div>
              <div class="qa-hint-block">
                <div class="qa-label">Alert Hub</div>
                <p class="qa-hint">
                  {{ rolloutLabel(summary.rollout.alert_hub?.mode) }}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.qa-kpi {
  text-decoration: none;
  color: inherit;
}

.qa-banner {
  margin: 0 0 4px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.4;
}

.qa-banner--warn {
  background: #fff7e6;
  border: 1px solid #f0c36d;
  color: #7a4e00;
}
</style>
